import { FC, useCallback, useState } from 'react'
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'

import { TFormValues } from './GroupsTab'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { CurrencyAlignment } from '../../../common/models/groups'
import FormGroup from '../../../core/components/FormGroup'
import {
  createGroupCurrency,
  deleteGroupCurrency,
  updateGroupCurrency,
} from '../../../core/services/groups'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import SelectField from '@/components/select-field'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

export const alignOptions = [
  { value: 'BEFORE_PRICE', label: 'Before Price' },
  { value: 'AFTER_PRICE', label: 'After Price' },
]

type Props = {
  currency: FieldArrayWithId<TFormValues, `groups.${number}.currencies`, 'id'>
  control: Control<TFormValues>
  errors: FieldErrors<TFormValues>
  register: UseFormRegister<TFormValues>
  currencies: Currency[]
  index: number
  remove: UseFieldArrayRemove
  groupIndex: number
  watch: UseFormWatch<TFormValues>
  setValue: UseFormSetValue<TFormValues>
  trigger: UseFormTrigger<TFormValues>
}

export const GroupCurrency: FC<Props> = ({
  errors,
  register,
  index,
  remove,
  groupIndex,
  watch,
  setValue,
  trigger,
  control,
}): JSX.Element => {
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)

  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()

  const { festivals } = useAppSelector((s) => s.app)

  const currency = watch(`groups.${groupIndex}.currencies.${index}`) || {
    name: '',
    symbol: '',
    alignment: 'BEFORE_PRICE' as const,
    main: false,
  }

  const [saving, setSaving] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const updateCurrency = useCallback(async () => {
    const valid = await trigger(`groups.${groupIndex}.currencies.${index}`)

    if (!valid) return
    try {
      const groupId = watch(`groups.${groupIndex}.id`)
      const currency = watch(`groups.${groupIndex}.currencies.${index}`)
      if (!groupId || !currency) return
      setSaving(true)
      if (currency.id) {
        await updateGroupCurrency(groupId, currency.id, {
          name: currency.name,
          symbol: currency.symbol,
          symbolAlignment: currency.alignment as CurrencyAlignment,
          id: currency.id,
        })
      } else {
        const firstFestivalId = watch(`groups.${groupIndex}.festivals.0.id`)
        const festivalName = firstFestivalId
          ? festivals.find((f) => f.id === firstFestivalId)?.platformName
          : undefined
        const { data, status } = await createGroupCurrency(
          groupId,
          {
            name: currency.name,
            symbol: currency.symbol,
            symbolAlignment: currency.alignment as CurrencyAlignment,
          },
          festivalName,
        )
        if (status === 200) {
          setValue(`groups.${groupIndex}.currencies.${index}.id`, data.id)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const deleteCurrency = useCallback(async () => {
    try {
      setSaving(true)
      const groupId = watch(`groups.${groupIndex}.id`)
      const currencyId = watch(`groups.${groupIndex}.currencies.${index}.id`)
      if (!groupId || !currencyId) return
      await deleteGroupCurrency(groupId, currencyId)
      remove(index as number)
      setDeleteConfirmation(false)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  return (
    <>
      {saving && <Loader />}
      <Card className="py-4 px-4 mt-2 !bg-monochrome-3-60 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <FormGroup label="Add Currency">
            <Input
              isInvalid={
                !!errors?.groups?.[groupIndex]?.currencies?.[index]?.name
                  ?.message
              }
              className="w-full"
              {...register(`groups.${groupIndex}.currencies.${index}.name`)}
              type="text"
              placeholder="Currency Name"
            />
          </FormGroup>
          <FormGroup label="Currency Symbol">
            <Input
              isInvalid={
                !!errors?.groups?.[groupIndex]?.currencies?.[index]?.symbol
                  ?.message
              }
              className="w-full"
              {...register(`groups.${groupIndex}.currencies.${index}.symbol`)}
              type="text"
              placeholder="Enter Currency Symbol"
            />
          </FormGroup>
          <Controller
            control={control}
            name={`groups.${groupIndex}.currencies.${index}.alignment`}
            render={({ field }) => (
              <SelectField
                outline
                label="Symbol Alignment"
                error={
                  errors?.groups?.[groupIndex]?.currencies?.[index]?.alignment
                    ?.message
                }
                triggerClassName="h-[44px] rounded-2xl"
                placeholder="Select Symbol Alignment"
                options={alignOptions}
                value={field.value}
                onValueChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="flex gap-2 justify-between mt-4 border-t border-monochrome-120 pt-4">
          <Button
            variant="destructive"
            type="button"
            className="w-[154px]"
            disabled={currency.main}
            onClick={() => {
              setDeleteIndex(index)
              setDeleteConfirmation(true)
            }}
          >
            Delete
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>

          <div className="flex gap-2 [&>*]:w-[154px]">
            <Button
              variant="secondary"
              className="flex items-center justify-center"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              className="flex items-center justify-center"
              onClick={updateCurrency}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Currency'}
        deleteFunc={() => {
          const saved = watch(`groups.${groupIndex}.currencies.${index}.saved`)
          if (saved) {
            deleteCurrency()
            return
          }
          remove(deleteIndex as number)
          setDeleteConfirmation(false)
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
